import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Form } from "react-bootstrap";
import { Input, Label } from "reactstrap";
import Select, { components } from 'react-select';
import { useLoader } from '../../_helper/LoaderContext';
import { addGlobalTestMethod, addMethod, getGlobalTestMethodList, updateMethod } from './service/method-service';
import { toast } from 'react-toastify';

// Custom Checkbox Option
const Option = (props) => {
    return (
        <components.Option {...props}>
            <div className="d-flex align-items-center py-1">
                <input
                    type="checkbox"
                    checked={props.isSelected}
                    onChange={() => null}
                    className="me-2"
                    style={{ pointerEvents: 'none' }}
                />
                <label className="m-0 cursor-pointer w-100">{props.label}</label>
            </div>
        </components.Option>
    );
};

// Select All Option
const allOption = { label: "Select All", value: "*" };

const customStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: '38px',
        height: '38px',
        overflow: 'hidden',
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: '38px',
        padding: '0 8px',
        overflowX: 'auto',
        overflowY: 'hidden',
        flexWrap: 'nowrap',
        whiteSpace: 'nowrap',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': {
            height: '6px',
        },
        '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
        },
    }),
    multiValue: (styles) => ({
        ...styles,
        backgroundColor: '#1b255b',
        margin: '2px',
        flexShrink: 0,
    }),
    multiValueLabel: (styles) => ({
        ...styles,
        color: '#fff',
        fontWeight: 500,
        fontSize: '12px',
    }),
    multiValueRemove: (styles) => ({
        ...styles,
        color: '#d32f2f',
        ':hover': {
            backgroundColor: '#ffcdd2',
            color: 'white',
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        fontSize: '13px',
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
    }),
};

export default function AddMethod({ handleClose, data, getList }) {
    const { t } = useTranslation();
    const [method, setMethod] = useState("");
    const [error, setError] = useState("");
    const [imported, setImported] = useState("add");
    const { setLoading } = useLoader();
    const [unitMaster, setUnitMaster] = useState([]);
    const [selectedGlobalUnits, setSelectedGlobalUnits] = useState([]);

    useEffect(() => {
        if (data) {
            setMethod(data.name || "");
        } else {
            setMethod("");
        }
    }, [data]);

    useEffect(() => {
        getGlobalTestMethod();
    }, []);

    // Fetch global test methods
    const getGlobalTestMethod = async () => {
        setLoading(true);
        try {
            const res = await getGlobalTestMethodList();
            if (res.data.success) {
                setUnitMaster(res.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching global units:", error);
        } finally {
            setLoading(false);
        }
    };

    const allOptionMemo = useMemo(() => ({ label: t("Select All"), value: "*" }), [t]);

    // Transform unitMaster into react-select format
    const unitOptions = useMemo(() => {
        const options = unitMaster.map((u) => ({
            label: u.name,
            value: u._id,
            description: u.description || "",
        }));
        return unitMaster.length > 0 ? [allOptionMemo, ...options] : [];
    }, [unitMaster, allOptionMemo]);

    // Handle Select Change with "Select All" logic
    const handleGlobalUnitChange = (selected) => {
        if (!selected) {
            setSelectedGlobalUnits([]);
            return;
        }

        const hasSelectAll = selected.some((opt) => opt.value === "*");

        if (hasSelectAll) {
            if (selectedGlobalUnits.length === unitMaster.length) {
                setSelectedGlobalUnits([]); // Deselect all
            } else {
                setSelectedGlobalUnits(
                    unitMaster.map((u) => ({
                        label: u.name,
                        value: u._id,
                    }))
                );
            }
        } else {
            const realSelections = selected.filter((opt) => opt.value !== "*");
            setSelectedGlobalUnits(realSelections);
        }
    };

    // Submit selected global units
    const handleSubmitSelected = async () => {
        const selectedIds = selectedGlobalUnits
            .filter((item) => item.value !== "*")
            .map((item) => item.value);

        if (selectedIds.length === 0) {
            toast.info(t("No units selected"));
            return;
        }
        const payload = {
            masterMethodologyIds: selectedIds,
        };
        setLoading(true);
        try {
            const res = await addGlobalTestMethod(payload);
            if (res.data.success == true) {
                toast.success(res.data.message);
                getList();
                setSelectedGlobalUnits([]);
                handleClose();
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const handleRadio = (e) => {
        const value = e.target.value;
        setImported(value);
    };

    const handleSubmit = async () => {
        if (!method.trim()) {
            setError(t("Please enter test method"));
            return;
        }

        setLoading(true);
        try {
            if (data) {
                const form_data = new FormData();
                form_data.append("name", method);
                form_data.append("testMethodId", data._id);
                const res = await updateMethod(form_data);
                if (res.data.success == true) {
                    toast.success(res.data.message);
                    getList();
                }
            } else {
                const form_data = new FormData();
                form_data.append("name", method);
                const res = await addMethod(form_data);
                if (res.data.success == true) {
                    toast.success(res.data.message);
                    getList();
                }
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }

        setError("");
        handleClose();
    };

    return (
        <>
            <Modal.Header closeButton className="py-2 px-3 border-bottom">
                <Modal.Title className="fs-6 fw-semibold m-0">
                    {data ? t("Edit Test Method") : t("Add Test Method")}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="py-2 px-3">
                {data ? (
                    <></>
                ) : (
                    <div className="d-flex tw-space-x-4 mb-2">
                        <div className="radio radio-primary">
                            <Input
                                id="customer-radio-add"
                                type="radio"
                                name="customerType"
                                value="add"
                                checked={imported === "add"}
                                onChange={handleRadio}
                            />
                            <Label for="customer-radio-add" className="mb-0 pe-2">
                                {t("Manual")}
                            </Label>
                        </div>
                        <div className="radio radio-primary">
                            <Input
                                id="customer-radio-gloabal"
                                type="radio"
                                name="customerType"
                                value="gloabal"
                                checked={imported === "gloabal"}
                                onChange={handleRadio}
                            />
                            <Label for="customer-radio-gloabal" className="mb-0 pe-2">
                                {t("Import From Database")}
                            </Label>
                        </div>
                        {selectedGlobalUnits.length > 0 && (
                            <Button
                                size="sm"
                                className="px-3"
                                onClick={handleSubmitSelected}
                                disabled={selectedGlobalUnits.length === 0}
                            >
                                {t("Import")} ({selectedGlobalUnits.length})
                            </Button>
                        )}
                    </div>
                )}

                {imported === "add" ? (
                    <>
                        <Form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSubmit();
                            }}
                        >
                            <Form.Group controlId="method" className="mb-0">
                                <Form.Label
                                    className="form-label mb-1"
                                    style={{ fontSize: "12.5px", fontWeight: 500 }}
                                >
                                    {t("Test Method Name")}{" "}
                                    <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    size="sm"
                                    placeholder={t("Enter test method name")}
                                    value={method}
                                    onChange={(e) => {
                                        setMethod(e.target.value);
                                        setError("");
                                    }}
                                    isInvalid={!!error}
                                    style={{ height: "36px", fontSize: "13px" }}
                                    autoFocus
                                />
                                {error && (
                                    <Form.Control.Feedback
                                        type="invalid"
                                        style={{ fontSize: "11px", marginTop: "2px" }}
                                    >
                                        {error}
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>
                        </Form>
                        <Modal.Footer className="py-2 px-0 border-top mt-2 d-flex justify-content-end gap-2">
                            <Button
                                variant="primary"
                                size="sm"
                                className="px-3"
                                onClick={handleSubmit}
                            >
                                {data ? t("Update") : t("Create")}
                            </Button>
                            <Button
                                variant="secondary"
                                size="sm"
                                className="px-3"
                                onClick={handleClose}
                            >
                                {t("Close")}
                            </Button>
                        </Modal.Footer>
                    </>
                ) : (
                    <>
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            options={unitOptions}
                            value={selectedGlobalUnits}
                            onChange={handleGlobalUnitChange}
                            placeholder={t("Add Test Methods from global list...")}
                            components={{ Option }}
                            styles={customStyles}
                            classNamePrefix="react-select"
                            isClearable={true}
                        />
                        <Modal.Footer className="py-2 px-0 border-top mt-2 d-flex justify-content-end gap-2">
                            <Button
                                size="sm"
                                className="px-3"
                                onClick={handleSubmitSelected}
                                disabled={selectedGlobalUnits.length === 0}
                            >
                                {t("Import")} ({selectedGlobalUnits.length})
                            </Button>
                            <Button
                                variant="secondary"
                                size="sm"
                                className="px-3"
                                onClick={handleClose}
                            >
                                {t("Close")}
                            </Button>
                        </Modal.Footer>
                    </>
                )}
            </Modal.Body>
        </>
    );
}
